/* eslint-disable camelcase */

import React, { useEffect, useRef } from 'react';

import am4themes_dataviz from '@amcharts/amcharts4/themes/dataviz';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
import { arrayOf, bool, shape, string } from 'prop-types';

import {
  ADSPENDDSP,
  ADSPENDPPC,
  ADSPENDTOTAL,
  CONVERSION,
  REVENUEDSP,
  REVENUEPPC,
  REVENUETOTAL,
  TACOS,
  TRAFFIC,
  UNITSSOLD,
  salesGraphColorSet as colorSet,
  salesGraphTooltipNames as tooltipNames,
} from '../../../../../../constants';

am4core.useTheme(am4themes_dataviz);
am4core.color('red');

const _ = require('lodash');

function SalesPerformanceGoalsChart({
  chartId,
  chartData,
  currencySymbol,
  selectedMetrics,
  selectedDF,
  isDashboard = false,
  groupBy,
}) {
  const chart = useRef(null);
  useEffect(() => {
    const sameAxisValue = [
      REVENUETOTAL,
      REVENUEPPC,
      //   'revenueOrganic',
      REVENUEDSP,
      // 'adSpend',
    ];

    const percentageAxisValue = [TACOS, CONVERSION];

    // function to generate complete tooltip html
    function generateToolTipHTML(tooltipValue, startDate, endDate) {
      let html = '';
      if (groupBy === 'weekly' || groupBy === 'monthly') {
        html = `<div style="font-size: 14px; font-family: Noah-Medium; padding:5px 0">
          From - ${startDate} To - ${endDate}
        </div>
        ${tooltipValue}`;
      } else {
        html = tooltipValue;
      }

      return html;
    }

    // create value axis function
    function createValueAxis() {
      const localValueAxis = chart.current.yAxes.push(
        new am4charts.ValueAxis(),
      );
      localValueAxis.renderer.grid.template.disabled = true;
      localValueAxis.cursorTooltipEnabled = false;
      localValueAxis.renderer.baseGrid.disabled = true;
      localValueAxis.numberFormatter = new am4core.NumberFormatter();
      localValueAxis.numberFormatter.numberFormat = `#.#a`;
      localValueAxis.extraMax = 0.005;
      localValueAxis.min = 0;
      localValueAxis.numberFormatter.bigNumberPrefixes = [
        { number: 1e3, suffix: 'K' },
        { number: 1e6, suffix: 'M' },
        { number: 1e9, suffix: 'B' },
      ];
      localValueAxis.numberFormatter.smallNumberPrefixes = [];
      return localValueAxis;
    }

    function renderTooltip(name, color, value, currency, percent, formatter) {
      const tooltipText = ` <ul style="padding:0; margin: 0 0 4px 0; max-width: 240px;">
      <li style="display: inline-block;">
        {' '}
        <div style="background-color: ${color};
        border: 1px solid white;
        border-radius: ${name === 'Goals' ? '2px' : '100%'};
        width: 10px;
        height: 10px;" />
      </li>
      <li style="display: inline-block;">
        <div style="color: #f4f6fc;
        text-transform: uppercase;
        font-size: 11px;
        padding-left: 5px;">${name} </div>
      </li>
      <li style="display: inline-block; float: right; margin-left: 25px;">
        <div style=" color: white;
        font-size: 16px; text-align: right;
       
        ">${currency !== null ? currency : ''}${
        formatter !== null
          ? `{${value}.formatNumber('${formatter}')}`
          : `{${value}}`
      }${percent !== null ? percent : ''}
      </div>
      </li>
    </ul>
  
    `;

      return tooltipText;
    }

    function bindValueAxisFormatter(item) {
      let format = '';
      if (sameAxisValue.includes(item)) {
        format = `${currencySymbol}#.#a`;
      } else if (item === UNITSSOLD || item === TRAFFIC) {
        format = `#.#a`;
      } else if (item === CONVERSION || item === TACOS) {
        format = `#.#'%'`;
      } else if (item === ADSPENDTOTAL) {
        format = `${currencySymbol}#.#a`;
      }
      return format;
    }

    chart.current = am4core.create(chartId, am4charts.XYChart);
    chart.current.data = chartData;
    chart.current.paddingRight = 20;
    chart.current.logo.disabled = true; // disable amchart logo
    chart.current.zoomOutButton.disabled = true; // disabled zoomout button

    // render X axis
    const dateAxis = chart.current.xAxes.push(new am4charts.DateAxis());
    dateAxis.renderer.minGridDistance = 50;
    dateAxis.renderer.grid.template.location = 0.5;
    dateAxis.renderer.labels.template.location = 0.5;
    dateAxis.dy = 10;
    dateAxis.gridIntervals.setAll([
      { timeUnit: 'day', count: 1 },
      { timeUnit: 'day', count: 5 },
      { timeUnit: 'day', count: 10 },
      { timeUnit: 'day', count: 15 },
      { timeUnit: 'day', count: 20 },
      { timeUnit: 'week', count: 1 },
      { timeUnit: 'week', count: 5 },
      { timeUnit: 'week', count: 10 },
      { timeUnit: 'month', count: 1 },
      { timeUnit: 'month', count: 2 },
      { timeUnit: 'month', count: 3 },
      { timeUnit: 'month', count: 6 },
    ]);
    dateAxis.cursorTooltipEnabled = false;
    dateAxis.periodChangeDateFormats.setKey('month', 'MMM');

    // create object of first value axis
    const valueAxis = createValueAxis();

    // Add cursor
    chart.current.cursor = new am4charts.XYCursor();
    chart.current.cursor.lineY.disabled = true;
    chart.current.cursor.lineX.disabled = true;
    chart.current.cursor.behavior = 'none';

    // user can select only two metrics when goals toggle is 'ON'
    const selectedMatricsFlag = _.size(selectedMetrics) <= 2;

    if (selectedMatricsFlag) {
      // create object of 2nd value axis
      let valueAxis2;
      const snapToSeries = [];
      let tooltipValue = '';
      let firstAxis = null;

      // loop for generate tooltip
      _.keys(selectedMetrics).map((item) => {
        const currentLabel = `${item}CurrentLabel`;
        const goalsLabel = `${item}GoalsLabel`;
        const colorCode = colorSet[item];

        tooltipValue = `${tooltipValue} ${_.startCase(item)}`;
        if (sameAxisValue.includes(item)) {
          tooltipValue = `${tooltipValue} ${renderTooltip(
            'Recent',
            colorCode,
            currentLabel,
            currencySymbol,
            null,
            '#,###',
          )}`;
          tooltipValue = `${tooltipValue} ${renderTooltip(
            'Goals',
            colorCode,
            goalsLabel,
            currencySymbol,
            null,
            '#,###',
          )}`;
        } else if (
          item === ADSPENDTOTAL ||
          item === ADSPENDPPC ||
          item === ADSPENDDSP
        ) {
          tooltipValue = `${tooltipValue} ${renderTooltip(
            'Recent',
            colorCode,
            currentLabel,
            currencySymbol,
            null,
            '#,###',
          )}`;
          tooltipValue = `${tooltipValue} ${renderTooltip(
            'Goals',
            colorCode,
            goalsLabel,
            currencySymbol,
            null,
            '#,###',
          )}`;
        } else if (percentageAxisValue?.includes(item)) {
          tooltipValue = `${tooltipValue} ${renderTooltip(
            'Recent',
            colorCode,
            currentLabel,
            null,
            '%',
            null,
          )}`;
          tooltipValue = `${tooltipValue} ${renderTooltip(
            'Goals',
            colorCode,
            goalsLabel,
            null,
            '%',
            null,
          )}`;
        } else if (item === 'traffic') {
          tooltipValue = `${tooltipValue} ${renderTooltip(
            'Recent',
            colorCode,
            currentLabel,
            null,
            '',
            null,
          )}`;
          tooltipValue = `${tooltipValue} ${renderTooltip(
            'Goals',
            colorCode,
            goalsLabel,
            null,
            '',
            null,
          )}`;
        }
        return '';
      });

      _.keys(selectedMetrics).map((item, index) => {
        const series = chart.current.series.push(new am4charts.LineSeries());
        const series2 = chart.current.series.push(new am4charts.LineSeries());
        const currentValue = `${item}Current`;
        const goalsValue = `${item}Goals`;
        const seriesName = `${item}Series`;
        const colorCode = colorSet[item];

        if (index === 0) {
          series.yAxis = valueAxis;
          series2.yAxis = valueAxis;
          valueAxis.numberFormatter.numberFormat = bindValueAxisFormatter(item);
          if (sameAxisValue.includes(item)) {
            firstAxis = 'currency';
          } else {
            firstAxis = 'other';
          }
        }
        if (index === 1) {
          if (sameAxisValue.includes(item)) {
            if (firstAxis === 'currency') {
              series.yAxis = valueAxis;
              series2.yAxis = valueAxis;
            } else {
              // create second value axis
              valueAxis2 = createValueAxis();
              series.yAxis = valueAxis2;
              series2.yAxis = valueAxis2;
              valueAxis2.renderer.opposite = true;
              valueAxis2.numberFormatter.numberFormat =
                bindValueAxisFormatter(item);
            }
          } else {
            valueAxis2 = createValueAxis();
            series.yAxis = valueAxis2;
            series2.yAxis = valueAxis2;
            valueAxis2.renderer.minGridDistance = 40;
            valueAxis2.renderer.opposite = true;
            valueAxis2.numberFormatter.numberFormat =
              bindValueAxisFormatter(item);
          }
        }

        series.dataFields.valueY = currentValue;
        series.dataFields.dateX = 'date';
        series.name = seriesName;
        series.strokeWidth = 2;
        // series.tooltipHTML = `${tooltipValue}`;
        series.stroke = am4core.color(colorCode);
        series.fill = am4core.color('#2e384d');
        series.adapter.add('tooltipHTML', (text, target) => {
          const dateRange = target.tooltipDataItem.dataContext;
          return generateToolTipHTML(
            tooltipValue,
            dateRange?.date,
            dateRange?.endDate,
          );
        });

        const circleBullet = series.bullets.push(new am4charts.CircleBullet());
        circleBullet.circle.fill = am4core.color(colorCode);
        circleBullet.circle.strokeWidth = 1;
        circleBullet.circle.radius = 5;

        series2.dataFields.valueY = goalsValue;
        series2.name = goalsValue;
        series2.dataFields.dateX = 'date';
        series2.strokeWidth = 2;
        series2.stroke = am4core.color(colorCode);
        // series2.tooltipHTML = `${tooltipValue}`;
        series2.fill = am4core.color('#2e384d');
        series2.strokeDasharray = '8,4';
        // series2.propertyFields.strokeDasharray = dashLine;
        series2.adapter.add('tooltipHTML', (text, target) => {
          const dateRange = target.tooltipDataItem.dataContext;
          return generateToolTipHTML(
            tooltipValue,
            dateRange?.date,
            dateRange?.endDate,
          );
        });

        const bullet = series2.bullets.push(new am4charts.Bullet());
        const square = bullet.createChild(am4core.Rectangle);
        square.fill = am4core.color(colorCode);
        square.width = 8;
        square.height = 8;
        square.horizontalCenter = 'middle';
        square.verticalCenter = 'middle';

        snapToSeries.push(series);
        snapToSeries.push(series2);
        return '';
      });

      chart.current.cursor.snapToSeries = snapToSeries;
    } else {
      // else part- for multiple metrics selected

      let firstAxis = null;
      let secondAxis = null;
      let thirdAxis = null;
      let valueAxis2;
      let valueAxis3;
      let valueAxis4;

      const snapToSeries = [];
      let tooltipValue = '';

      // loop for genearate tooltip
      _.keys(selectedMetrics).map((item) => {
        const currentLabel = `${item}CurrentLabel`;

        if (sameAxisValue.includes(item)) {
          tooltipValue = `${tooltipValue} ${renderTooltip(
            tooltipNames[item],
            colorSet[item],
            currentLabel,
            currencySymbol,
            null,
            '#,###',
          )}`;
        } else if (item === TRAFFIC || item === UNITSSOLD) {
          tooltipValue = `${tooltipValue} ${renderTooltip(
            tooltipNames[item],
            colorSet[item],
            currentLabel,
            null,
            null,
            '#,###',
          )}`;
        } else if (item === CONVERSION || item === TACOS) {
          tooltipValue = `${tooltipValue} ${renderTooltip(
            tooltipNames[item],
            colorSet[item],
            currentLabel,
            null,
            '%',
            null,
          )}`;
        } else if (
          item === ADSPENDTOTAL ||
          item === ADSPENDPPC ||
          item === ADSPENDDSP
        ) {
          tooltipValue = `${tooltipValue} ${renderTooltip(
            tooltipNames[item],
            colorSet[item],
            currentLabel,
            currencySymbol,
            null,
            '#,###',
          )}`;
        }
        return '';
      });

      _.keys(selectedMetrics).map((item, index) => {
        const series = chart.current.series.push(new am4charts.LineSeries());

        if (index === 0) {
          series.yAxis = valueAxis;
          valueAxis.numberFormatter.numberFormat = bindValueAxisFormatter(item);
          if (sameAxisValue.includes(item)) {
            firstAxis = 'currency';
          } else {
            firstAxis = 'other';
          }
        } else if (index === 1) {
          if (sameAxisValue.includes(item)) {
            if (firstAxis === 'currency') {
              series.yAxis = valueAxis;
              valueAxis.numberFormatter.numberFormat =
                bindValueAxisFormatter(item);
            } else {
              if (valueAxis2 === undefined) {
                valueAxis2 = createValueAxis();
              }
              series.yAxis = valueAxis2;
              valueAxis2.renderer.opposite = true;
              valueAxis2.numberFormatter.numberFormat =
                bindValueAxisFormatter(item);
              secondAxis = 'currency';
            }
          } else {
            if (valueAxis2 === undefined) {
              valueAxis2 = createValueAxis();
            }
            series.yAxis = valueAxis2;
            valueAxis2.renderer.opposite = true;
            valueAxis2.numberFormatter.numberFormat =
              bindValueAxisFormatter(item);
            secondAxis = 'other';
          }
        } else if (index === 2) {
          if (sameAxisValue.includes(item)) {
            if (firstAxis === 'currency') {
              series.yAxis = valueAxis;
              valueAxis.numberFormatter.numberFormat =
                bindValueAxisFormatter(item);
            } else if (secondAxis === 'currency') {
              if (valueAxis2 === undefined) {
                valueAxis2 = createValueAxis();
              }
              series.yAxis = valueAxis2;
              valueAxis2.numberFormatter.numberFormat =
                bindValueAxisFormatter(item);
            } else {
              if (valueAxis3 === undefined) {
                valueAxis3 = createValueAxis();
              }
              series.yAxis = valueAxis3;
              valueAxis3.renderer.opposite = true;
              valueAxis3.numberFormatter.numberFormat =
                bindValueAxisFormatter(item);
              valueAxis.renderer.line.strokeOpacity = 0;
              valueAxis2.renderer.line.strokeOpacity = 0;
              valueAxis3.renderer.line.strokeOpacity = 0;
              valueAxis.renderer.labels.template.disabled = true;
              valueAxis2.renderer.labels.template.disabled = true;
              valueAxis3.renderer.labels.template.disabled = true;
              thirdAxis = 'currency';
            }
          } else if (secondAxis === null) {
            if (valueAxis2 === undefined) {
              valueAxis2 = createValueAxis();
            }

            series.yAxis = valueAxis2;
            valueAxis2.renderer.opposite = true;
            valueAxis2.numberFormatter.numberFormat =
              bindValueAxisFormatter(item);
            secondAxis = 'other';
          } else {
            if (valueAxis3 === undefined) {
              valueAxis3 = createValueAxis();
            }
            series.yAxis = valueAxis3;
            valueAxis3.renderer.opposite = true;
            valueAxis3.numberFormatter.numberFormat =
              bindValueAxisFormatter(item);
            valueAxis.renderer.line.strokeOpacity = 0;
            valueAxis2.renderer.line.strokeOpacity = 0;
            valueAxis3.renderer.line.strokeOpacity = 0;
            valueAxis.renderer.labels.template.disabled = true;
            valueAxis2.renderer.labels.template.disabled = true;
            valueAxis3.renderer.labels.template.disabled = true;
            thirdAxis = 'other';
          }
        } else if (index === 3) {
          if (sameAxisValue.includes(item)) {
            if (firstAxis === 'currency') {
              series.yAxis = valueAxis;
              valueAxis.numberFormatter.numberFormat =
                bindValueAxisFormatter(item);
            } else if (secondAxis === 'currency') {
              if (valueAxis2 === undefined) {
                valueAxis2 = createValueAxis();
              }
              series.yAxis = valueAxis2;
              valueAxis2.renderer.opposite = true;
              valueAxis2.numberFormatter.numberFormat =
                bindValueAxisFormatter(item);
            } else if (thirdAxis === 'currency') {
              if (valueAxis3 === undefined) {
                valueAxis3 = createValueAxis();
              }
              series.yAxis = valueAxis3;
              valueAxis3.renderer.opposite = true;
              valueAxis3.numberFormatter.numberFormat =
                bindValueAxisFormatter(item);
              valueAxis.renderer.line.strokeOpacity = 0;
              valueAxis2.renderer.line.strokeOpacity = 0;
              valueAxis3.renderer.line.strokeOpacity = 0;
              valueAxis.renderer.labels.template.disabled = true;
              valueAxis2.renderer.labels.template.disabled = true;
              valueAxis3.renderer.labels.template.disabled = true;
            } else {
              if (valueAxis4 === undefined) {
                valueAxis4 = createValueAxis();
              }
              series.yAxis = valueAxis4;
              valueAxis4.numberFormatter.numberFormat =
                bindValueAxisFormatter(item);
              valueAxis4.renderer.opposite = true;
              valueAxis4.renderer.line.strokeOpacity = 0;
              valueAxis4.renderer.labels.template.disabled = true;
              valueAxis.renderer.line.strokeOpacity = 0;
              valueAxis2.renderer.line.strokeOpacity = 0;
              valueAxis3.renderer.line.strokeOpacity = 0;
              valueAxis.renderer.labels.template.disabled = true;
              valueAxis2.renderer.labels.template.disabled = true;
              valueAxis3.renderer.labels.template.disabled = true;
            }
          } else if (secondAxis === null) {
            if (valueAxis2 === undefined) {
              valueAxis2 = createValueAxis();
            }
            series.yAxis = valueAxis2;
            valueAxis2.renderer.opposite = true;
            valueAxis2.numberFormatter.numberFormat =
              bindValueAxisFormatter(item);
            secondAxis = 'other';
          } else if (thirdAxis === null) {
            if (valueAxis3 === undefined) {
              valueAxis3 = createValueAxis();
            }
            series.yAxis = valueAxis3;
            valueAxis3.renderer.opposite = true;
            valueAxis3.numberFormatter.numberFormat =
              bindValueAxisFormatter(item);
            thirdAxis = 'other';
            valueAxis.renderer.line.strokeOpacity = 0;
            valueAxis2.renderer.line.strokeOpacity = 0;
            valueAxis3.renderer.line.strokeOpacity = 0;
            valueAxis.renderer.labels.template.disabled = true;
            valueAxis2.renderer.labels.template.disabled = true;
            valueAxis3.renderer.labels.template.disabled = true;
          } else {
            if (valueAxis4 === undefined) {
              valueAxis4 = createValueAxis();
            }
            series.yAxis = valueAxis4;
            valueAxis4.numberFormatter.numberFormat =
              bindValueAxisFormatter(item);
            valueAxis4.renderer.opposite = true;
            valueAxis4.renderer.line.strokeOpacity = 0;
            valueAxis4.renderer.labels.template.disabled = true;
            valueAxis.renderer.line.strokeOpacity = 0;
            valueAxis2.renderer.line.strokeOpacity = 0;
            valueAxis3.renderer.line.strokeOpacity = 0;
            valueAxis.renderer.labels.template.disabled = true;
            valueAxis2.renderer.labels.template.disabled = true;
            valueAxis3.renderer.labels.template.disabled = true;
          }
        }

        series.dataFields.valueY = `${item}Current`;
        series.dataFields.dateX = 'date';
        series.name = `${item}Series`;
        series.strokeWidth = 2;
        // series.tooltipHTML = `${tooltipValue}`;
        series.stroke = am4core.color(colorSet[item]);
        series.fill = am4core.color('#2e384d');
        series.adapter.add('tooltipHTML', (text, target) => {
          const dateRange = target.tooltipDataItem.dataContext;
          return generateToolTipHTML(
            tooltipValue,
            dateRange?.date,
            dateRange?.endDate,
            'location1',
          );
        });

        const circleBullet = series.bullets.push(new am4charts.CircleBullet());
        circleBullet.circle.fill = am4core.color(colorSet[item]);
        circleBullet.circle.strokeWidth = 1;
        circleBullet.circle.radius = 5;

        snapToSeries.push(series);
        return '';
      });

      chart.current.cursor.snapToSeries = snapToSeries;
    }
    return () => chart.current && chart.current.dispose();
  }, [
    chartId,
    chartData,
    currencySymbol,
    selectedMetrics,
    selectedDF,
    isDashboard,
    groupBy,
  ]);

  return <div id={chartId} style={{ width: '100%', height: '510px' }} />;
}

export default SalesPerformanceGoalsChart;

SalesPerformanceGoalsChart.defaultProps = {
  isDashboard: false,
  chartData: [],
  currencySymbol: '',
  selectedMetrics: {},
  selectedDF: '',
  groupBy: '',
};

SalesPerformanceGoalsChart.propTypes = {
  isDashboard: bool,
  chartId: string.isRequired,
  chartData: arrayOf(shape({})),
  currencySymbol: string,
  selectedMetrics: shape({}),
  selectedDF: string,
  groupBy: string,
};
